import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/constants";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Camera, Check, AlertTriangle, LogIn, Trash2 } from "lucide-react";
import VitalsForm from "@/components/crm/VitalsForm";
import PatientAvatar from "@/components/crm/PatientAvatar";
import MaskedPhoneInput from "@/components/crm/MaskedPhoneInput";

const MAX_PHOTO_MB = 5;

/**
 * Reception's check-in step: correct the patient's details, assign the doctor,
 * take a photo and record vitals — all before the doctor sees them.
 *
 * Step 1 (the status change) is a transaction boundary; steps 2-4 are a repair
 * queue. We never roll the check-in back on a later failure: the patient record
 * and its write-once patient_no are already allocated, so reverting would strand
 * an orphan number. Forward-only repair instead.
 */
export default function CheckIn() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const [appt, setAppt] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ name: "", mobile: "", email: "", gender: "", age: "", city: "", doctor_id: "", branch_id: "", note: "" });
  const [vitals, setVitals] = useState({});
  const [previousVitals, setPreviousVitals] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [checkedIn, setCheckedIn] = useState(null);   // patient_id once step 1 lands
  const [failures, setFailures] = useState([]);       // which of steps 2-4 need a retry

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/appointments/${appointmentId}`)
      .then(({ data }) => {
        setAppt(data);
        const p = data.patient || {};
        setForm({
          name: p.name || data.patient_name || "",
          mobile: p.mobile || data.mobile || "",
          email: p.email || "",
          gender: p.gender || "",
          age: p.age != null ? String(p.age) : "",
          city: p.city || "",
          doctor_id: data.doctor_id || "",
          branch_id: data.branch_id || "",
          note: "",
        });
        setVitals(data.vitals || {});
        if (data.status === "checked_in" || data.status === "in_consultation") {
          setCheckedIn(data.patient_id);
        }
        if (data.patient_id) {
          api.get(`/patients/${data.patient_id}/consultation-history`)
            .then((r) => {
              const past = (r.data.consultations || []).find((c) => c.vitals);
              if (past?.vitals) setPreviousVitals(past.vitals);
            })
            .catch(() => {});
        }
      })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); navigate("/appointments"); })
      .finally(() => setLoading(false));
  }, [appointmentId, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users", { params: { role: "doctor" } }).then((r) => setDoctors(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" /></div>;
  if (!appt) return null;

  const pickPhoto = (file) => {
    if (!file) return;
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      toast.error(`Photo must be under ${MAX_PHOTO_MB} MB`);
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const p = appt.patient || {};
  const detailChanges = () => {
    const out = {};
    if (form.name.trim() && form.name.trim() !== (p.name || appt.patient_name)) out.name = form.name.trim();
    if (form.mobile.trim() && form.mobile.trim() !== (p.mobile || appt.mobile)) out.mobile = form.mobile.trim();
    if (form.email !== (p.email || "")) out.email = form.email;
    if (form.gender !== (p.gender || "")) out.gender = form.gender;
    if (form.city !== (p.city || "")) out.city = form.city;
    const age = form.age === "" ? null : parseInt(form.age, 10);
    if (age !== (p.age ?? null) && age != null) out.age = age;
    return out;
  };
  const hasVitals = Object.values(vitals).some((v) => v !== "" && v != null);

  // Steps 2-4. Each is independent — one failure must not abort the others, and
  // all three are idempotent upserts so Retry is always safe.
  const runFollowUps = async (patientId, only = null) => {
    const failed = [];
    const want = (step) => !only || only.includes(step);

    const changes = detailChanges();
    if (want("details") && Object.keys(changes).length) {
      try { await api.put(`/patients/${patientId}`, changes); }
      catch { failed.push("details"); }
    }
    if (want("vitals") && hasVitals) {
      try {
        await api.post("/consultations/check-in-vitals", {
          appointment_id: appt.id,
          vitals: Object.fromEntries(Object.entries(vitals).filter(([, v]) => v !== "" && v != null)),
        });
      } catch { failed.push("vitals"); }
    }
    // Photo last and never blocking: lowest-value artefact, most likely to fail.
    if (want("photo") && photoFile) {
      try {
        const fd = new FormData();
        fd.append("file", photoFile);
        await api.post(`/patients/${patientId}/photo`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      } catch { failed.push("photo"); }
    }
    return failed;
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Patient name is required"); return; }
    if (!form.doctor_id) {
      toast.error("Assign a doctor — the prescription is issued in their name");
      return;
    }
    if (!form.branch_id && branches.length > 1) {
      toast.error("Select a branch — it becomes part of the patient ID");
      return;
    }
    setSaving(true);
    try {
      let patientId = checkedIn;
      if (!patientId) {
        // Step 1: the only required call. doctor_id rides along so it's one
        // atomic write and one history row, not two.
        const { data: updated } = await api.put(`/appointments/${appt.id}`, {
          status: "checked_in",
          status_note: form.note || "",
          doctor_id: form.doctor_id,
          ...(form.branch_id && form.branch_id !== appt.branch_id ? { branch_id: form.branch_id } : {}),
        });
        patientId = updated.patient_id;
        setCheckedIn(patientId);
      } else if (form.doctor_id !== appt.doctor_id) {
        await api.put(`/appointments/${appt.id}`, { doctor_id: form.doctor_id });
      }

      const failed = await runFollowUps(patientId);
      setFailures(failed);
      if (failed.length) {
        toast.warning(`Checked in. Couldn't save: ${failed.join(", ")}.`);
        load();
      } else {
        toast.success("Checked in");
        navigate("/appointments");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const retry = async () => {
    setSaving(true);
    const failed = await runFollowUps(checkedIn, failures);
    setFailures(failed);
    if (failed.length) toast.warning(`Still couldn't save: ${failed.join(", ")}.`);
    else { toast.success("Saved"); navigate("/appointments"); }
    setSaving(false);
  };

  const removePhoto = async () => {
    if (photoPreview) { setPhotoFile(null); setPhotoPreview(null); return; }
    if (!checkedIn) return;
    try { await api.delete(`/patients/${checkedIn}/photo`); toast.success("Photo removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const locked = !!checkedIn;

  return (
    <div className="space-y-5 pb-16">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/appointments")} className="h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2">
          <LogIn className="h-5 w-5 text-navy" />
          <div>
            <h2 className="font-display text-xl font-semibold text-navy">Check In</h2>
            <p className="text-xs text-muted-foreground">
              {[appt.service, formatDateTime(appt.scheduled_at)].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
      </div>

      {locked && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">
          <Check className="h-4 w-4" /> Already checked in — you can still correct details, add a photo or record vitals.
        </div>
      )}
      {failures.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" /> Not saved: {failures.join(", ")}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Patient identity */}
        <div className="space-y-4">
          <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
            <h3 className="mb-4 font-display font-semibold text-navy">Patient</h3>
            <div className="flex items-start gap-5">
              <div className="text-center">
                <label className="cursor-pointer" title="Upload photo">
                  {photoPreview ? (
                    <img src={photoPreview} alt="" className="h-20 w-20 rounded-full object-cover" />
                  ) : (
                    <PatientAvatar
                      patientId={appt.patient_id} name={form.name}
                      photoPath={p.photo_path} updatedAt={p.photo_updated_at} size="lg"
                      className="h-20 w-20 text-2xl"
                    />
                  )}
                  <span className="mt-1.5 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
                    <Camera className="h-3 w-3" /> {p.photo_path || photoPreview ? "Change" : "Photo"}
                  </span>
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => pickPhoto(e.target.files?.[0])} data-testid="checkin-photo" />
                </label>
                {(p.photo_path || photoPreview) && (
                  <button onClick={removePhoto} className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground hover:text-red-600">
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                )}
              </div>
              <div className="grid flex-1 grid-cols-2 gap-3">
                <Field label="Name *">
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="checkin-name" />
                </Field>
                <Field label="Mobile">
                  <MaskedPhoneInput value={form.mobile} onChange={(v) => set("mobile", v)} lockedValue={p.mobile || appt.mobile} data-testid="checkin-mobile" />
                </Field>
                <Field label="Gender">
                  <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {["Male", "Female", "Other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Age">
                  <Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)} />
                </Field>
                <Field label="Email">
                  <Input value={form.email} onChange={(e) => set("email", e.target.value)} />
                </Field>
                <Field label="City">
                  <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
                </Field>
              </div>
            </div>
            {p.patient_no && (
              <p className="mt-3 border-t border-navy/10 pt-3 font-mono text-xs text-muted-foreground">
                {p.patient_no}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
            <h3 className="mb-4 font-display font-semibold text-navy">Visit</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Doctor *">
                <Select value={form.doctor_id} onValueChange={(v) => set("doctor_id", v)}>
                  <SelectTrigger data-testid="checkin-doctor"><SelectValue placeholder="Assign a doctor" /></SelectTrigger>
                  <SelectContent>
                    {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">The prescription is issued in their name.</p>
              </Field>
              <Field label={appt.branch_id ? "Branch" : "Branch *"}>
                <Select value={form.branch_id} onValueChange={(v) => set("branch_id", v)} disabled={!!appt.branch_id}>
                  <SelectTrigger data-testid="checkin-branch"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!appt.branch_id && <p className="mt-1 text-xs text-muted-foreground">Becomes part of the patient ID.</p>}
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Note for the doctor (optional)">
                <Textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)}
                  placeholder="Anything the doctor should know" disabled={locked} />
              </Field>
            </div>
          </div>
        </div>

        {/* Vitals — the reason this is a page and not a dialog */}
        <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display font-semibold text-navy">Vitals</h3>
            {hasVitals && <span className="text-xs font-medium text-green-700">Recorded</span>}
          </div>
          <VitalsForm vitals={vitals} onChange={setVitals} previousVitals={previousVitals} />
          <p className="mt-4 text-xs text-muted-foreground">
            The doctor sees these before the consultation. You can leave them blank and add them later.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-navy/10 bg-cream/40 px-5 py-4">
        <p className="text-sm text-muted-foreground">
          {locked ? "Details, photo and vitals can be updated any time before the doctor finishes."
                  : "Checking in creates the patient record and puts them on the waiting board."}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/appointments")}>Cancel</Button>
          {locked && failures.length > 0 ? (
            <Button onClick={retry} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="checkin-retry">
              {saving ? "Retrying…" : "Retry"}
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="checkin-confirm">
              {saving ? "Saving…" : locked ? "Save Changes" : "Check In"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
